import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const ANCHOR_PARAM = 'anchor';
const OFFSET_PARAM = 'anchorOffset';
const SCROLL_TOP_PARAM = 'anchorScrollTop';

/**
 * Every param this hook owns. Changing the view (payment option, mode, tab, buyer) rebuilds the
 * quote list, which makes a saved position meaningless — those callers clear these.
 */
export const SCROLL_ANCHOR_PARAMS = [ANCHOR_PARAM, OFFSET_PARAM, SCROLL_TOP_PARAM] as const;

const WRITE_DEBOUNCE_MS = 150;
const QUOTE_SELECTOR = '[data-quote-id]';

function readNumberParam(searchParams: URLSearchParams, name: string): number {
  const raw = searchParams.get(name);
  if (raw === null) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Persists the shopper's place in the quote list to the URL so a full page reload lands them
 * back where they were.
 *
 * Anchored on the quote nearest the top of the viewport rather than a raw pixel offset, because
 * the list height changes with filters, premium adjustments and expanded cards — a pixel offset
 * would land somewhere arbitrary.
 *
 * Three params, with distinct jobs:
 * - `anchor` — id of the quote under the top edge.
 * - `anchorOffset` — how far *into* that card the viewport top sat, so a partially-scrolled card
 *   is restored exactly. Add it back to the card's `offsetTop`; it is not a scroll position.
 * - `anchorScrollTop` — the raw scroll position, used only when the anchored quote is no longer
 *   in the list (filters changed, a different carrier set came back). Approximate by design.
 */
export function useShopInsuranceScrollAnchor(scrollContainerRef: {
  current: HTMLElement | null;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasRestoredRef = useRef(false);
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read once into a ref: the params are rewritten while scrolling, and re-reading them would
  // make the restore effect fight its own writes.
  const pendingRestoreRef = useRef<{
    anchorId: string | null;
    offset: number;
    scrollTop: number;
  } | null>(null);
  if (pendingRestoreRef.current === null && !hasRestoredRef.current) {
    pendingRestoreRef.current = {
      anchorId: searchParams.get(ANCHOR_PARAM),
      offset: readNumberParam(searchParams, OFFSET_PARAM),
      scrollTop: readNumberParam(searchParams, SCROLL_TOP_PARAM),
    };
  }

  /** Attempts the restore. Returns false while the list has not rendered yet, so the caller retries. */
  const restore = useCallback(() => {
    const container = scrollContainerRef.current;
    const pending = pendingRestoreRef.current;
    if (!container || !pending?.anchorId) return true;

    const cards = container.querySelectorAll<HTMLElement>(QUOTE_SELECTOR);
    if (cards.length === 0) return false;

    const anchorCard = Array.from(cards).find(card => card.dataset.quoteId === pending.anchorId);

    if (anchorCard) {
      // offsetTop is relative to the nearest positioned ancestor, which is the scroll container
      // itself (it carries `relative`). anchorOffset is how far the shopper had scrolled into
      // the card, so it adds to the card's top to reproduce the original scrollTop.
      container.scrollTop = Math.max(0, anchorCard.offsetTop + pending.offset);
    } else if (pending.scrollTop > 0) {
      container.scrollTop = pending.scrollTop;
    }

    return true;
  }, [scrollContainerRef]);

  useEffect(() => {
    if (hasRestoredRef.current) return;

    if (restore()) {
      hasRestoredRef.current = true;
      return;
    }

    // Quotes arrive asynchronously; keep looking until the first card renders, then stop.
    const observer = new MutationObserver(() => {
      if (restore()) {
        hasRestoredRef.current = true;
        observer.disconnect();
      }
    });

    const container = scrollContainerRef.current;
    if (!container) return;

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [restore, scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);

      writeTimerRef.current = setTimeout(() => {
        const cards = Array.from(container.querySelectorAll<HTMLElement>(QUOTE_SELECTOR));

        // Back at the top: drop the params rather than pinning the first card, so a fresh visit
        // to a clean URL is not silently anchored.
        if (container.scrollTop <= 0 || cards.length === 0) {
          setSearchParams(
            current => {
              const next = new URLSearchParams(current);
              next.delete(ANCHOR_PARAM);
              next.delete(OFFSET_PARAM);
              next.delete(SCROLL_TOP_PARAM);
              return next;
            },
            { replace: true },
          );
          return;
        }

        // Topmost card whose bottom edge is still below the viewport top.
        const topCard =
          cards.find(card => card.offsetTop + card.offsetHeight > container.scrollTop) ??
          cards[cards.length - 1];

        const quoteId = topCard.dataset.quoteId;
        if (!quoteId) return;

        setSearchParams(
          current => {
            const next = new URLSearchParams(current);
            next.set(ANCHOR_PARAM, quoteId);
            next.set(OFFSET_PARAM, String(Math.round(container.scrollTop - topCard.offsetTop)));
            next.set(SCROLL_TOP_PARAM, String(Math.round(container.scrollTop)));
            return next;
          },
          { replace: true },
        );
      }, WRITE_DEBOUNCE_MS);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    };
  }, [scrollContainerRef, setSearchParams]);
}
